/* ----------------------------------------------------------------------------
   Single source of truth for site-wide identity, contact details and SEO
   defaults. Everything that needs the canonical origin — <Seo>, the JSON-LD
   builders, the sitemap generator, robots.txt — reads it from here.

   SITE_URL must be the production origin with no trailing slash. Set
   VITE_SITE_URL in the Netlify environment so preview deploys and the eventual
   custom domain both resolve correctly.
---------------------------------------------------------------------------- */

export const SITE_URL = (
  import.meta.env?.VITE_SITE_URL ?? "https://sableboxx.com"
).replace(/\/$/, "")

export const SITE = {
  name: "Sableboxx",
  legalName: "Sableboxx",
  /** Appended to every page title except the homepage. */
  titleSuffix: "Sableboxx",
  tagline: "Software & Digital Infrastructure Company",
  description:
    "Sableboxx designs, builds and implements digital systems that help businesses and institutions automate operations, manage information and deliver services more efficiently.",
  locale: "en_NG",
  lang: "en",
  email: "hello@sableboxx.com",
  phone: "+234 810 869 9059",
  /** Tel: href form — digits and a leading plus only. */
  phoneHref: "+2348108699059",
  /*
    Sableboxx does not publish a street address. Only the operating locality is
    stated, and the Organization JSON-LD deliberately carries no PostalAddress
    (see organizationSchema in seo.tsx) — an address claimed in structured data
    is an address published.
  */
  address: {
    city: "Abuja",
    region: "FCT",
    country: "NG",
    countryName: "Nigeria",
  },
  /** Display string for the "where are you" line in the footer and contact page. */
  locationLabel: "Abuja, Nigeria · Working globally",
  /** Real profile URLs — these feed the Organization sameAs. */
  social: [
    "https://www.instagram.com/sableboxx",
    "https://www.linkedin.com/company/sableboxx",
    "https://x.com/sableboxx",
  ] as string[],
  socialLinks: [
    { label: "Instagram", href: "https://www.instagram.com/sableboxx" },
    { label: "LinkedIn", href: "https://www.linkedin.com/company/sableboxx" },
    { label: "X", href: "https://x.com/sableboxx" },
  ],
  twitterHandle: "@sableboxx",
  foundingYear: "2023",
} as const

/** Default social share image. 1200x630, the size every platform crops to. */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.png`

export const OG_IMAGE_WIDTH = 1200
export const OG_IMAGE_HEIGHT = 630

/**
 * Normalises a share image to Open Graph dimensions.
 *
 * Content covers are stored at whatever size they were uploaded, which social
 * platforms letterbox badly. Both hosts we serve covers from can crop on
 * demand, so we ask for a correctly-proportioned image and then honestly
 * declare og:image:width/height.
 *
 * Unsplash takes query parameters; Cloudinary takes a transformation segment in
 * the path, inserted directly after "/upload/". Any other host is passed
 * through untouched and left undeclared, since we cannot know its dimensions.
 */
export function ogImage(src: string): { url: string; sized: boolean } {
  const abs = absoluteUrl(src)
  if (abs === DEFAULT_OG_IMAGE) return { url: abs, sized: true }

  try {
    const url = new URL(abs)

    if (url.hostname.endsWith("unsplash.com")) {
      url.searchParams.set("w", String(OG_IMAGE_WIDTH))
      url.searchParams.set("h", String(OG_IMAGE_HEIGHT))
      url.searchParams.set("fit", "crop")
      return { url: url.toString(), sized: true }
    }

    /*
      Only rewrite a URL that has not been transformed already, so re-saving a
      cover cannot stack "w_1200,h_630/w_1200,h_630/..." onto the path.
    */
    if (url.hostname === "res.cloudinary.com" && url.pathname.includes("/upload/")) {
      const transform = `w_${OG_IMAGE_WIDTH},h_${OG_IMAGE_HEIGHT},c_fill,g_auto,f_auto,q_auto`
      if (!url.pathname.includes(`/upload/${transform}/`)) {
        url.pathname = url.pathname.replace("/upload/", `/upload/${transform}/`)
      }
      return { url: url.toString(), sized: true }
    }
  } catch {
    /* not a parseable URL — fall through */
  }
  return { url: abs, sized: false }
}

/** Resolves any path or absolute URL to an absolute URL on the canonical origin. */
export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl
  return `${SITE_URL}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`
}

/**
 * Canonical URL for a route. Strips trailing slashes (except the root) so
 * "/work/" and "/work" never compete as separate URLs in the index.
 */
export function canonicalUrl(pathname: string): string {
  const clean = pathname.replace(/\/+$/, "")
  return `${SITE_URL}${clean || "/"}`
}
